import { FunctionComponent, useMemo } from "react";

type Props = {
  title?: string;
  label: string;
  name?: string;
  className?: string;
  wrapperClassName?: string;
  inputClassName?: string;
  placeholder?: string;
  value?: string;
  type?: string;
  onChange?: (e: React.FormEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  onFocus?: (e: React.FocusEvent<HTMLInputElement, Element>) => void;
  errorMsg?: string;
  valid?: boolean;
  disabled?: boolean;
  autocomplete?: string;
  maxLength?: number;
  hint?: string;
  icon?: string | React.ReactNode;
  showValidIcon?: boolean;
  [key: string]: any;
};

const FormInput: FunctionComponent<Props> = (props: Props) => {
  const {
    title,
    label,
    name,
    className = "",
    wrapperClassName = "",
    inputClassName = "",
    placeholder,
    value,
    type = "text",
    onChange,
    onBlur,
    onFocus,
    errorMsg,
    valid,
    disabled,
    autocomplete,
    maxLength,
    hint,
    icon,
    showValidIcon = false,
    ...rest
  } = props;

  const hasError = !!errorMsg;
  const isValid = !!valid && !hasError;

  const stateStyle: React.CSSProperties = useMemo(() => {
    if (hasError)
      return {
        borderColor: "#dc2626",
        boxShadow: "none",
      };
    if (isValid)
      return {
        borderColor: "#16a34a",
        boxShadow: "none",
      };
    return {
      borderColor: "#d1d5db",
      boxShadow: "none",
    };
  }, [hasError, isValid]);

  const focusRing = hasError
    ? "focus:outline-none focus:ring-4 focus:ring-red-100 focus:border-red-500"
    : isValid
    ? "focus:outline-none focus:ring-4 focus:ring-green-100 focus:border-green-500"
    : "focus:outline-none focus:ring-4 focus:ring-blue-100 focus:border-blue-500";

  return (
    <div className={`${wrapperClassName} ${className}`}>
      {title && <p className="mb-2 text-left">{title}</p>}
      <label
        htmlFor={name}
        className="block text-sm font-bold mb-1 text-left"
        style={{ color: "#0f1111" }}
      >
        {label}
      </label>
      <div className="relative">
        {icon && (
          <span
            className="absolute left-3 top-1/2 pointer-events-none flex items-center"
            style={{ transform: "translateY(-50%)", color: "#6b7280" }}
            aria-hidden="true"
          >
            {typeof icon === "string" ? (
              <i className="material-icons" style={{ fontSize: "20px" }}>{icon}</i>
            ) : (
              icon
            )}
          </span>
        )}
        <input
          id={name}
          name={name}
          type={type}
          value={value ?? ""}
          placeholder={placeholder}
          onChange={onChange}
          onBlur={onBlur}
          onFocus={onFocus}
          disabled={disabled}
          autoComplete={autocomplete}
          maxLength={maxLength}
          aria-invalid={hasError || undefined}
          className={`block w-full rounded-xl bg-white text-base ${icon ? "pl-10" : "pl-3"} pr-10 py-2 border disabled:bg-gray-50 disabled:text-gray-500 disabled:cursor-not-allowed transition-colors ${focusRing} ${inputClassName}`}
          style={{
            color: "#111827",
            ...stateStyle,
          }}
          {...rest}
        />
        {isValid && showValidIcon && (
          <svg
            className="absolute right-3 top-1/2 w-5 h-5 pointer-events-none"
            style={{
              transform: "translateY(-50%)",
              color: "#16a34a",
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M5 13l4 4L19 7"
            />
          </svg>
        )}
        {hasError && (
          <svg
            className="absolute right-3 top-1/2 w-5 h-5 pointer-events-none"
            style={{
              transform: "translateY(-50%)",
              color: "#dc2626",
            }}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            aria-hidden="true"
          >
            <circle cx="12" cy="12" r="10" strokeWidth={2} />
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              strokeWidth={2.5}
              d="M12 8v4M12 16h.01"
            />
          </svg>
        )}
      </div>
      {hasError ? (
        <p
          className="mt-1 text-xs text-left"
          style={{ color: "#dc2626" }}
        >
          {errorMsg}
        </p>
      ) : hint ? (
        <p
          className="mt-1 text-xs text-left"
          style={{ color: "#565959" }}
        >
          {hint}
        </p>
      ) : null}
    </div>
  );
};

export { FormInput, FormInput as default };
